import React from 'react';
import PropTypes from 'prop-types';
import Option from 'components/option';
import { withForwardRef } from 'hoc';
import { get, noop } from 'lodash';

function OptionGroup({ Option, children, ...props }) {
  const [selectedValue, setSelectedValues] = React.useState(getDefaultValue());

  function getDefaultValue() {
    if (props.value) {
      return props.value;
    }

    return props.multiSelect ? [] : undefined;
  }

  function onChange(currentValue) {
    return function (checked) {
      if (props.disabled) {
        return;
      }

      const newValue = props.multiSelect
        ? multiSelect(currentValue, checked)
        : currentValue;

      setSelectedValues(newValue);

      props.onChange(newValue);
    };
  }

  function multiSelect(value, checked) {
    if (checked) {
      return [...selectedValue, value];
    }

    return selectedValue.filter((option) => option !== value);
  }

  function mapOptions(item, key) {
    const { valueField, labelField, errorMessage, disabled } = props;
    // We check if item is react element...that means we have options defined as component children instead of options prop.
    let { label, value } = item.props || item;

    const isChecked = props.multiSelect
      ? selectedValue.includes(value)
      : selectedValue === value;

    const useDataAsValue = labelField && valueField;

    if (useDataAsValue) {
      label = get(item, labelField);
      value = get(item, valueField);
    }

    const optionProperties = {
      key,
      label,
      disabled,
      value: isChecked,
      invalid: Boolean(errorMessage),
      name: String(value),
      onChange: onChange(value),
      inGroup: true,
      withForm: props.withForm,
      errorMessage,
      wasSubmitted: props.wasSubmitted,
    };

    return <Option {...optionProperties} />;
  }

  function renderOptions() {
    const { options, optionsMap, childrenOptions } = props;

    if (childrenOptions) {
      return React.Children.map(childrenOptions, mapOptions);
    }

    if (optionsMap) {
      return Object.entries(optionsMap)
        .map(([label, value]) => ({ label, value }))
        .map(mapOptions);
    }

    return options.map(mapOptions);
  }

  return children({
    ...props,
    invalid: Boolean(props.errorMessage),
    renderOptions,
    selectedValue,
    setSelectedValues,
    onChange,
  });
}

OptionGroup.propTypes = {
  children: PropTypes.func.isRequired,
  childrenOptions: PropTypes.node,
  Option: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  display: PropTypes.oneOf(['column', 'row']),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  labelField: PropTypes.string,
  name: PropTypes.string.isRequired,
  multiSelect: PropTypes.bool,
  options: PropTypes.array,
  optionsMap: PropTypes.object,
  onChange: PropTypes.func,
  valueField: PropTypes.string,
  value: PropTypes.any,
  // We use this property to toggle option group from controlled to uncontrolled
  withForm: PropTypes.bool,
};

OptionGroup.defaultProps = {
  childrenOptions: null,
  display: 'row',
  disabled: false,
  errorMessage: '',
  label: '',
  labelField: '',
  multiSelect: true,
  options: [],
  optionsMap: undefined,
  onChange: noop,
  valueField: '',
  value: undefined,
  withForm: false,
};

export function withOptionGroup(StyledOptionGroup, StyledOption) {
  function WithOptionGroup({ children, ...props }) {
    return (
      <OptionGroup {...props} childrenOptions={children} Option={StyledOption}>
        {(props) => <StyledOptionGroup {...props} />}
      </OptionGroup>
    );
  }

  WithOptionGroup.propTypes = {
    children: PropTypes.node,
  };

  WithOptionGroup.defaultProps = {
    children: null,
  };

  const OptionGroupComponent = withForwardRef(WithOptionGroup);

  OptionGroupComponent.Option = Option;

  return OptionGroupComponent;
}

export default OptionGroup;
